import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
    Box,
    Typography,
    List,
    ListItemButton,
    ListItemIcon,
    ListItemText,
    Collapse,
    FormControlLabel,
    Switch,
    Chip,
    TextField,
    InputAdornment,
    IconButton,
    ToggleButton,
    ToggleButtonGroup,
    Tooltip,
    Card,
    CardContent,
    CardHeader,
    Paper,
    Button,
    CircularProgress,
    Stack,
    Alert,
    AlertTitle
} from "@mui/material";
import {
    Search as SearchIcon,
    Clear as ClearIcon,
    ExpandMore,
    ChevronRight,
    Lock as LockIcon,
    Business as ClientIcon,
    AccountTree as AccountIcon,
    Layers as GroupIcon,
    SubdirectoryArrowRight as InheritsArrow
} from "@mui/icons-material";
import { TabPanel } from "@mui/lab";
import styled from "@emotion/styled";
import { useDispatch } from "react-redux";
import {
    searchBillingEntities,
    toggleBillingEntity
} from "../redux/ducks/billingEntities";

const StyledTabPanel = styled(TabPanel)(({ theme }) => ({
    padding: theme.spacing(3, 0, 0, 0)
}));

const SearchRow = styled(Box)(({ theme }) => ({
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(2),
    marginBottom: theme.spacing(2),
    flexWrap: "wrap"
}));

const SearchField = styled(TextField)({
    flex: 1,
    minWidth: 240,
});

const ScopeToggle = styled(ToggleButtonGroup)(({ theme }) => ({
    "& .MuiToggleButton-root": {
        textTransform: "none",
        paddingLeft: theme.spacing(2),
        paddingRight: theme.spacing(2)
    }
}));

const StatsLine = styled(Typography)(({ theme }) => ({
    color: theme.palette.text.secondary,
    fontSize: theme.typography.size.md,
    marginBottom: theme.spacing(2)
}));

const TreeContainer = styled(Box)(({ theme }) => ({
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: theme.shape.borderRadius * 2,
    background: theme.palette.background.paper,
    overflow: "hidden"
}));

const NodeRow = styled(ListItemButton, {
    shouldForwardProp: (prop) => prop !== "$depth" && prop !== "$pending"
})(({ theme, $depth, $pending }) => ({
    paddingLeft: theme.spacing(2 + $depth * 2),
    paddingTop: theme.spacing(1),
    paddingBottom: theme.spacing(1),
    borderLeft: $pending
        ? `3px solid ${theme.palette.warning.main}`
        : "3px solid transparent",
    backgroundColor: $pending ? theme.palette.action.hover : "transparent",
    cursor: "default",
    "&:hover": {
        backgroundColor: $pending ? theme.palette.action.hover : theme.palette.action.hover
    }
}));

const ChevronIcon = styled(ListItemIcon)({
    minWidth: 28,
    cursor: "pointer"
});

const NarrowIcon = styled(ListItemIcon)({
    minWidth: 28
});

const SpacerBox = styled(Box)({
    width: 20
});

const NodeLabelRow = styled(Box)(({ theme }) => ({
    display: "flex",
    alignItems: "center",
    flexWrap: "wrap",
    gap: theme.spacing(1)
}));

const NodeName = styled(Typography, {
    shouldForwardProp: (prop) => prop !== "$strong",
})(({ theme, $strong }) => ({
    fontWeight: $strong ? theme.typography.fontWeightSemiBold : theme.typography.fontWeightMedium,
}));

const PendingLabel = styled(Typography)(({ theme }) => ({
    fontWeight: theme.typography.fontWeightSemiBold,
}));

const TermsLine = styled(Typography)(({ theme }) => ({
    color: theme.palette.text.secondary,
    fontSize: theme.typography.size.sm,
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(0.5)
}));

const InheritsPill = styled(Chip)(({ theme }) => ({
    height: 18,
    fontSize: theme.typography.size.xs,
    background: theme.palette.internal?.light || theme.palette.action.selected,
    color: theme.palette.internal?.dark || theme.palette.text.primary,
    "& .MuiChip-label": { paddingLeft: 6, paddingRight: 6 }
}));

const LevelNote = styled(Typography)(({ theme }) => ({
    color: theme.palette.text.secondary,
    fontSize: theme.typography.size.sm,
    marginBottom: theme.spacing(2)
}));

const MatchedViaChip = styled(Chip)(({theme}) => ({
    height: 18,
    fontSize: theme.typography.size.xs,
    fontStyle: "italic"
}));

const EntityCheckboxBox = styled(Box)(({ theme }) => ({
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(0.5),
    minWidth: 110,
    justifyContent: "flex-end"
}));

const EmptyState = styled(Box)(({ theme }) => ({
    padding: theme.spacing(8),
    textAlign: "center",
    color: theme.palette.text.secondary
}));

const ApplyErrorAlert = styled(Alert)(({ theme }) => ({
    marginBottom: theme.spacing(2)
}));

const PendingFooter = styled(Paper)(({ theme }) => ({
    position: "sticky",
    bottom: theme.spacing(2),
    marginTop: theme.spacing(2),
    padding: theme.spacing(1.5, 2),
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: theme.spacing(2),
    border: `1px solid ${theme.palette.warning.main}`,
    background: theme.palette.background.paper
}));

const formatCurrency = (n) =>
    typeof n === "number"
        ? n.toLocaleString("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 })
        : "";

const summarizeTerms = (terms) => {
    if (!terms) return "—";
    const t = terms.type;
    if (t === "Retainer") {
        const hours = terms.hours ? `${terms.hours}h` : null;
        const charge = terms.charge ? `${formatCurrency(terms.charge)}/mo` : null;
        return ["Retainer", hours, charge].filter(Boolean).join(" · ");
    }
    if (t === "Fixed Price") return "Fixed Price";
    if (t === "Hourly") return "Hourly";
    if (t === "Internal") return "Internal";
    if (t === "Non Billable") return "Non Billable";
    if (t === "Parent") return "Parent (inherits)";
    return t || "—";
};

const CHILD_LEVELS = {
    Client: [{ key: "accounts", type: "Account" }],
    Account: [{ key: "groups", type: "Group" }],
    Group: []
};

const TYPE_ICON = {
    Client: ClientIcon,
    Account: AccountIcon,
    Group: GroupIcon
};

const FORCED_TYPES = {
    Account: ["Retainer", "Fixed Price"],
    Group: ["Retainer", "Fixed Price"]
};

const MATCHED_VIA = {
    "account-name": {
        label: "matched via account name",
        tooltip: "This client came up because one of its accounts matches what you typed."
    },
    "group-name": {
        label: "matched via group name",
        tooltip: "This client came up because one of its groups has a name matching what you typed."
    }
};

const childrenOf = (entityType, node) =>
    (CHILD_LEVELS[entityType] || []).flatMap((level) =>
        (node[level.key] || []).map((child) => ({ entityType: level.type, node: child }))
    );

const applyTermsInTree = (entityType, node, targetType, targetId, billingTerms) => {
    if (entityType === targetType && node._id === targetId) return { ...node, billingTerms };
    const next = { ...node };
    for (const level of CHILD_LEVELS[entityType] || []) {
        if (!node[level.key]) continue;
        next[level.key] = node[level.key].map((child) => applyTermsInTree(level.type, child, targetType, targetId, billingTerms));
    }
    return next;
};

const pruneToBillingEntities = (entityType, node) => {
    const next = { ...node };
    let keptDescendant = false;
    for (const level of CHILD_LEVELS[entityType] || []) {
        if (!node[level.key]) continue;
        const kept = node[level.key].map((child) => pruneToBillingEntities(level.type, child)).filter(Boolean);
        next[level.key] = kept;
        if (kept.length > 0) keptDescendant = true;
    }
    if (node.billingTerms?.billingEntity === true || keptDescendant) return next;
    return null;
};

const countBillingEntities = (entityType, node) =>
    (node.billingTerms?.billingEntity ? 1 : 0) +
    childrenOf(entityType, node).reduce((sum, child) => sum + countBillingEntities(child.entityType, child.node), 0);

const countByLevels = (entityType, node, levels) =>
    (levels.includes(entityType) ? 1 : 0) +
    childrenOf(entityType, node).reduce((sum, child) => sum + countByLevels(child.entityType, child.node, levels), 0);

const lockReasonFor = (entityType, terms) => {
    if (entityType === "Client") return "Clients always invoice on their own, so this cannot be switched off.";
    if ((FORCED_TYPES[entityType] || []).includes(terms?.type)) {
        return `A ${entityType.toLowerCase()} on ${terms.type} billing terms always invoices on its own, so this cannot be switched off.`;
    }
    return null;
};

const useDebouncedValue = (value, delay) => {
    const [debounced, setDebounced] = useState(value);
    useEffect(() => {
        const id = setTimeout(() => setDebounced(value), delay);
        return () => clearTimeout(id);
    }, [value, delay]);
    return debounced;
};

const EntityRow = ({
    entityType,
    node,
    depth,
    parent,
    expandedIds,
    onToggleExpand,
    pendingState,
    onTogglePending,
    busyId
}) => {
    const busy = busyId === node._id;
    const children = childrenOf(entityType, node);
    const hasChildren = children.length > 0;
    const isExpanded = expandedIds.has(node._id);
    const lockReason = lockReasonFor(entityType, node.billingTerms);
    const isLocked = !!lockReason;
    const currentValue = node.billingTerms?.billingEntity === true;
    const pendingValue = pendingState[`${entityType}:${node._id}`];
    const effectiveValue = pendingValue !== undefined ? pendingValue : currentValue;
    const isPending = pendingValue !== undefined && pendingValue !== currentValue;
    const TypeIcon = TYPE_ICON[entityType] || AccountIcon;
    const matchedVia = MATCHED_VIA[node.matchedVia];

    const showInherits = node.billingTerms?.type === "Parent" && parent?.billingTerms?.type === "Retainer";

    const handleCheckboxChange = (e) => {
        if (isLocked || busy) return;
        onTogglePending(entityType, node._id, e.target.checked);
    };

    return (
        <>
            <NodeRow
                $depth={depth}
                $pending={isPending}
                disableRipple
                onClick={(e) => {
                    if (hasChildren) {
                        e.preventDefault();
                        onToggleExpand(node._id);
                    }
                }}
            >
                {hasChildren ? (
                    <ChevronIcon
                        onClick={(e) => {
                            e.stopPropagation();
                            onToggleExpand(node._id);
                        }}
                    >
                        {isExpanded ? <ExpandMore fontSize="small" /> : <ChevronRight fontSize="small" />}
                    </ChevronIcon>
                ) : (
                    <NarrowIcon>
                        <SpacerBox />
                    </NarrowIcon>
                )}
                <NarrowIcon>
                    <Tooltip title={entityType}>
                        <TypeIcon fontSize="small" color={entityType === "Client" ? "primary" : "action"} />
                    </Tooltip>
                </NarrowIcon>
                <ListItemText
                    disableTypography
                    primary={
                        <NodeLabelRow>
                            <NodeName variant="body2" $strong={entityType === "Client"}>
                                {node.name}
                            </NodeName>
                            {matchedVia && (
                                <Tooltip title={matchedVia.tooltip}>
                                    <MatchedViaChip label={matchedVia.label} size="small" variant="outlined" />
                                </Tooltip>
                            )}
                        </NodeLabelRow>
                    }
                    secondary={
                        <TermsLine variant="caption">
                            <span>{summarizeTerms(node.billingTerms)}</span>
                            {showInherits && (
                                <Tooltip title={`Its hours count against the retainer declared on ${parent.name}. The base charge stays on ${parent.name}; this entity shows its own consumption and any overage.`}>
                                    <InheritsPill
                                        size="small"
                                        icon={<InheritsArrow style={{ fontSize: 12 }} />}
                                        label={`retainer from ${parent.name}`}
                                    />
                                </Tooltip>
                            )}
                        </TermsLine>
                    }
                />
                <EntityCheckboxBox onClick={(e) => e.stopPropagation()}>
                    {busy && <CircularProgress size={14} />}
                    {isLocked && (
                        <Tooltip title={lockReason}>
                            <LockIcon fontSize="small" color="disabled" />
                        </Tooltip>
                    )}
                    <Tooltip title={isLocked ? lockReason : effectiveValue ? "Billing entity — click to unset" : "Not a billing entity — click to set"}>
                        <span>
                            <Switch
                                size="small"
                                checked={effectiveValue}
                                disabled={isLocked || busy}
                                onChange={handleCheckboxChange}
                            />
                        </span>
                    </Tooltip>
                </EntityCheckboxBox>
            </NodeRow>
            {hasChildren && (
                <Collapse in={isExpanded}>
                    <List disablePadding>
                        {children.map((child) => (
                            <EntityRow
                                key={`${child.entityType}:${child.node._id}`}
                                entityType={child.entityType}
                                node={child.node}
                                depth={depth + 1}
                                parent={node}
                                expandedIds={expandedIds}
                                onToggleExpand={onToggleExpand}
                                pendingState={pendingState}
                                onTogglePending={onTogglePending}
                                busyId={busyId}
                            />
                        ))}
                    </List>
                </Collapse>
            )}
        </>
    );
};

const BillingEntities = ({ tabValue }) => {
    const dispatch = useDispatch();
    const [searchTerm, setSearchTerm] = useState("");
    const [billingOnly, setBillingOnly] = useState(false);
    const [showClosed, setShowClosed] = useState(false);
    const [data, setData] = useState({ clients: [], matchedVia: {} });
    const [loading, setLoading] = useState(false);
    const [expandedIds, setExpandedIds] = useState(new Set());
    const [pendingState, setPendingState] = useState({});
    const [busyId, setBusyId] = useState(null);
    const [applyErrors, setApplyErrors] = useState([]);

    const debouncedQuery = useDebouncedValue(searchTerm.trim(), 400);

    useEffect(() => {
        // Empty query is meaningful only in Billing-only mode (server returns
        // every billing entity). In All mode an empty query stays empty.
        if (tabValue !== "entities") return;
        if (!debouncedQuery && !billingOnly) {
            setData({ clients: [], matchedVia: {} });
            return;
        }
        setLoading(true);
        dispatch(
            searchBillingEntities(
                debouncedQuery,
                billingOnly,
                (resp) => {
                    setLoading(false);
                    if (!resp) return;
                    const matched = resp.matchedVia || {};
                    const decorated = {
                        clients: (resp.clients || []).map((c) => ({
                            ...c,
                            matchedVia: matched[c._id] || (debouncedQuery ? "name" : null)
                        })),
                        matchedVia: matched,
                        matchedVia: matched
                    };
                    setData(decorated);
                    // Auto-expand only when the user is actively searching — when
                    // browsing the full billing-only list, expanding everything
                    // turns navigation into a wall of text.
                    setExpandedIds(
                        debouncedQuery
                            ? new Set(decorated.clients.map((c) => c._id))
                            : new Set()
                    );
                },
                billingOnly === false
            )
        );
    }, [tabValue, debouncedQuery, billingOnly, dispatch]);

    const handleToggleExpand = useCallback((id) => {
        setExpandedIds((prev) => {
            const next = new Set(prev);
            if (next.has(id)) next.delete(id);
            else next.add(id);
            return next;
        });
    }, []);

    const handleTogglePending = useCallback((entityType, entityId, value) => {
        setPendingState((prev) => {
            const key = `${entityType}:${entityId}`;
            const next = { ...prev };
            next[key] = value;
            return next;
        });
    }, []);

    const pendingEntries = useMemo(() => {
        const entries = [];
        const findOriginal = (key) => {
            const [type, id] = key.split(":");
            const walk = (entityType, node) => {
                if (entityType === type && node._id === id) {
                    return { type, id, current: !!node.billingTerms?.billingEntity, name: node.name };
                }
                for (const child of childrenOf(entityType, node)) {
                    const found = walk(child.entityType, child.node);
                    if (found) return found;
                }
                return null;
            };
            for (const c of data.clients) {
                const found = walk("Client", c);
                if (found) return found;
            }
            return null;
        };
        for (const [key, value] of Object.entries(pendingState)) {
            const orig = findOriginal(key);
            if (!orig) continue;
            if (orig.current !== value) entries.push({ ...orig, value });
        }
        return entries;
    }, [pendingState, data.clients]);

    const applyPending = useCallback(async () => {
        const failures = [];
        setApplyErrors([]);
        for (const entry of pendingEntries) {
            setBusyId(entry.id);
            const result = await new Promise((resolve) => {
                dispatch(toggleBillingEntity(entry.type, entry.id, entry.value, resolve));
            });
            if (result?.ok) {
                setData((prev) => ({
                    ...prev,
                    clients: prev.clients.map((c) => applyTermsInTree("Client", c, entry.type, entry.id, result.data.billingTerms))
                }));
            } else {
                failures.push({
                    key: `${entry.type}:${entry.id}`,
                    name: entry.name,
                    type: entry.type,
                    message: result?.message || "The change could not be saved."
                });
            }
            setPendingState((prev) => {
                const next = { ...prev };
                delete next[`${entry.type}:${entry.id}`];
                return next;
            });
        }
        setBusyId(null);
        setApplyErrors(failures);
    }, [pendingEntries, dispatch]);

    const discardPending = useCallback(() => {
        setPendingState({});
        setApplyErrors([]);
    }, []);

    const visibleClients = useMemo(() => {
        if (showClosed) return data.clients;
        return data.clients
            .map((c) => ({
                ...c,
                accounts: (c.accounts || [])
                    .map((a) => ({ ...a, groups: (a.groups || []).filter((g) => !g.billingClosed) }))
                    .filter((a) => a.billingTerms?.billingEntity === true || a.groups.length > 0)
            }))
            .filter((c) => c.accounts.length > 0 || c.matchedVia === "name" || c.billingTerms?.type !== "Hourly");
    }, [data.clients, showClosed]);

    const closedGroupCount = useMemo(
        () => data.clients.reduce(
            (sum, c) => sum + (c.accounts || []).reduce(
                (n, a) => n + (a.groups || []).filter((g) => g.billingClosed).length, 0), 0),
        [data.clients]
    );

    const renderedClients = useMemo(() => {
        if (!billingOnly) return visibleClients;
        return visibleClients.map((c) => pruneToBillingEntities("Client", c)).filter(Boolean);
    }, [visibleClients, billingOnly]);

    const billingEntityCount = useMemo(
        () => visibleClients.reduce((sum, c) => sum + countBillingEntities("Client", c), 0),
        [visibleClients]
    );

    const deepLevelCount = useMemo(
        () => visibleClients.reduce((sum, c) => sum + countByLevels("Client", c, ["Group"]), 0),
        [visibleClients]
    );

    if (tabValue !== "entities") return null;

    const showEmptyTypePrompt = !debouncedQuery && !billingOnly;
    const isLoadingFresh = loading && data.clients.length === 0;

    let stats;
    if (showEmptyTypePrompt) {
        stats = "Type to search by client, account or group name.";
    } else if (debouncedQuery) {
        stats = `${data.clients.length} client${data.clients.length === 1 ? "" : "s"} matched · ${billingEntityCount} billing ${billingEntityCount === 1 ? "entity" : "entities"}`;
    } else {
        stats = `${billingEntityCount} billing ${billingEntityCount === 1 ? "entity" : "entities"} across ${data.clients.length} client${data.clients.length === 1 ? "" : "s"}`;
    }

    return (
        <StyledTabPanel value="entities">
            <Card variant="outlined">
                <CardHeader
                    title={<Typography variant="h6">Billing Entities</Typography>}
                    subheader="Mark which clients, accounts and groups produce their own invoice. In All, a search lists every group under the accounts it finds. Projects are not listed here: every project billing entity is Fixed Price, which is set on the project itself."
                />
                <CardContent>
                    <SearchRow>
                        <SearchField
                            size="small"
                            placeholder="Search by client, account or group name…"
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <SearchIcon fontSize="small" />
                                    </InputAdornment>
                                ),
                                endAdornment: searchTerm ? (
                                    <InputAdornment position="end">
                                        <IconButton size="small" onClick={() => setSearchTerm("")}>
                                            <ClearIcon fontSize="small" />
                                        </IconButton>
                                    </InputAdornment>
                                ) : null
                            }}
                        />
                        <ScopeToggle
                            size="small"
                            exclusive
                            value={billingOnly ? "billing" : "all"}
                            onChange={(_, v) => v && setBillingOnly(v === "billing")}
                        >
                            <ToggleButton value="all">All</ToggleButton>
                            <ToggleButton value="billing">Billing only</ToggleButton>
                        </ScopeToggle>
                        <Tooltip title="A completed group with every payment already paid cannot produce another invoice. Retainer groups are never hidden, because they bill every period.">
                            <FormControlLabel
                                control={<Switch size="small" checked={showClosed} onChange={(e) => setShowClosed(e.target.checked)} />}
                                label="Show closed groups"
                            />
                        </Tooltip>
                    </SearchRow>

                    <StatsLine>
                        {stats}
                        {!showClosed && closedGroupCount > 0 &&
                            ` · ${closedGroupCount} closed group${closedGroupCount === 1 ? "" : "s"} hidden`}
                    </StatsLine>

                    {applyErrors.length > 0 && (
                        <ApplyErrorAlert severity="error" onClose={() => setApplyErrors([])}>
                            <AlertTitle>
                                {applyErrors.length === 1 ? "One change was not saved" : `${applyErrors.length} changes were not saved`}
                            </AlertTitle>
                            {applyErrors.map((failure) => (
                                <Typography key={failure.key} variant="body2">
                                    {failure.name} ({failure.type}): {failure.message}
                                </Typography>
                            ))}
                        </ApplyErrorAlert>
                    )}

                    {!showEmptyTypePrompt && deepLevelCount === 0 && renderedClients.length > 0 && (
                        <LevelNote>
                            {billingOnly
                                ? "No groups are listed here. Billing only lists a group once it is a billing entity of its own, which is automatic on Retainer or Fixed Price billing terms and can otherwise be switched on from its own row in All."
                                : "No groups are listed here. The accounts found have no groups. Projects are not listed on this page: every project billing entity is Fixed Price, which is set on the project itself and cannot be changed here."}
                        </LevelNote>
                    )}

                    {showEmptyTypePrompt ? (
                        <EmptyState>
                            <Typography variant="body2">Type to search, or switch to Billing only to see every billing entity.</Typography>
                        </EmptyState>
                    ) : isLoadingFresh ? (
                        <EmptyState>
                            <CircularProgress size={20} />
                        </EmptyState>
                    ) : renderedClients.length === 0 ? (
                        <EmptyState>
                            <Typography variant="body2">Nothing matched that search. Try part of a client, account or group name.</Typography>
                        </EmptyState>
                    ) : (
                        <TreeContainer>
                            <List disablePadding>
                                {renderedClients.map((client) => (
                                    <EntityRow
                                        key={client._id}
                                        entityType="Client"
                                        node={client}
                                        depth={0}
                                        parent={null}
                                        expandedIds={expandedIds}
                                        onToggleExpand={handleToggleExpand}
                                        pendingState={pendingState}
                                        onTogglePending={handleTogglePending}
                                        busyId={busyId}
                                    />
                                ))}
                            </List>
                        </TreeContainer>
                    )}
                </CardContent>
            </Card>

            {pendingEntries.length > 0 && (
                <PendingFooter elevation={6}>
                    <Stack direction="row" spacing={1} alignItems="center">
                        <PendingLabel variant="body2">
                            {pendingEntries.length} change{pendingEntries.length === 1 ? "" : "s"} pending
                        </PendingLabel>
                        <Typography variant="caption" color="text.secondary">
                            Review before applying
                        </Typography>
                    </Stack>
                    <Stack direction="row" spacing={1}>
                        <Button size="small" onClick={discardPending} disabled={busyId !== null}>
                            Discard
                        </Button>
                        <Button
                            size="small"
                            variant="contained"
                            onClick={applyPending}
                            disabled={busyId !== null}
                        >
                            {busyId !== null ? "Applying…" : "Apply"}
                        </Button>
                    </Stack>
                </PendingFooter>
            )}
        </StyledTabPanel>
    );
};

export default BillingEntities;
